import { useCallback, useState } from 'react';
import { EventType } from '@core/customEvent';
import { useGlobalStore } from '@core/hooks/useGlobalStore';
import { logWarning } from '@core/logging';
import { useContextParams } from '@core/params/context';
import { cartDisplayedCallbackSelector, cartUpdatedCallbackSelector, } from '@core/shopping/model';
import { customEmbedEvent } from '@embed/customEmbedEvent';
/**
 * Interacts directly with the _fwn.shopping API. All state management of the cart
 * is handled by the embedding platform instead of the player store
 */
export function useShoppingApi(video) {
    const cartUpdatedCallback = useGlobalStore(cartUpdatedCallbackSelector);
    const cartDisplayedCallback = useGlobalStore(cartDisplayedCallbackSelector);
    const params = useContextParams();
    const [loading, setLoading] = useState(false);
    const getCart = useCallback(() => {
        if (cartDisplayedCallback) {
            return cartDisplayedCallback();
        }
        else {
            logWarning('cartDisplayed callback not set');
            return Promise.reject(undefined);
        }
    }, [cartDisplayedCallback]);
    const getCartQuantity = useCallback(async (product, unit) => {
        const unitActual = unit ?? product.product_units[0];
        try {
            const cart = await getCart();
            return (cart?.find((item) => item.unitId === unitActual.unit_ext_id)
                ?.quantity ?? 0);
        }
        catch (e) {
            logWarning('Failed to get cart quantity', e);
            return Promise.reject(undefined);
        }
    }, [getCart]);
    const addToCart = useCallback(({ product, unit, quantity = 1, callback, }) => {
        setLoading(true);
        getCartQuantity(product, unit)
            .then((currentCartQuantity) => {
            if (!cartUpdatedCallback) {
                logWarning('cartUpdated callback not set');
                setLoading(false);
                callback?.(false);
                return;
            }
            cartUpdatedCallback({
                product,
                productUnit: unit ?? product.product_units[0],
                quantity: quantity + currentCartQuantity,
                previousQuantity: currentCartQuantity,
                video,
            })
                .then((quantity) => {
                customEmbedEvent({
                    type: EventType.FW_SHOPPING_UPDATEQUANTITY,
                    params,
                    data: {
                        product,
                        product_unit: unit ?? product.product_units[0],
                        quantity,
                        previousQuantity: currentCartQuantity,
                    },
                });
                setLoading(false);
                callback?.(true);
            })
                .catch((e) => {
                logWarning('Failed to update cart', e);
                setLoading(false);
                callback?.(false);
            });
        })
            .catch((e) => {
            logWarning('Failed to get cart quantity', e);
            setLoading(false);
            callback?.(false);
        });
    }, [getCartQuantity, cartUpdatedCallback, video, params]);
    return {
        addToCart,
        loading,
    };
}
